import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, onMounted, getCurrentInstance } from "vue";
import { Auth } from "aws-amplify";
import { NolaCoinClient } from "@/lib/clients/nolaCoinClient";
import { BillingClient } from "@/lib/clients/billingClient";
import { FishProduct } from "@/lib/models/nolaCoin";
import { CreateOneTimeCheckoutSessionInput } from "@/lib/models/billing";
import { showNotifyDialog } from "@/lib/dialog";
import { Plan } from "@/lib/models/user";
import recommendPc from "@/assets/img/premium/recommend_pc.png";
import recommendSp from "@/assets/img/premium/recommend_sp.png";
import ChevronLeftIcon from "icons/ChevronLeft.vue";
import PlusIcon from "icons/Plus.vue";
import LoadingSpinner from "@/components/atoms/LoadingSpinner.vue";
import { NolaAnalytics, NolaItemId } from "@/lib/utils/analytics";

// APIクライアントのインスタンス

export default /*#__PURE__*/_defineComponent({
  __name: 'PurchaseFish',
  setup(__props) {

const nolaCoinClient = new NolaCoinClient();
const billingClient = new BillingClient();

// Vue インスタンス
const instance = getCurrentInstance();
const store = instance?.proxy?.$store;

// アナリティクス
const nolaAnalytics = new NolaAnalytics(instance?.proxy.$gtm!, store!);

// i18n
const t = (key: string, params?: Record<string, unknown>): string =>
  instance?.proxy?.$t(key, params) as string;

// リアクティブな状態管理
const fishList = ref<FishProduct[]>([]);
const isLoading = ref(false);
const error = ref<string | null>(null);
const initialFishLoading = ref(true); // 初期Fish読み込み状態

// 計算プロパティ
const formattedCurrentFish = computed(() => store?.getters["fishModule/formattedCurrentFish"]);
const formattedPrepaidFish = computed(() => store?.getters["fishModule/formattedPrepaidFish"]);
const formattedFreeFish = computed(() => store?.getters["fishModule/formattedFreeFish"]);
const storeFishLoading = computed(() => store?.getters["fishModule/isLoading"]);
const fishError = computed(() => store?.getters["fishModule/error"]);

// プレミアム関連
const plan = computed(() => store?.getters["billingModule/plan"]);
const isFreePlan = computed(() => plan.value === Plan.free);
const bannerImageUrl = computed(() =>
  // 画面幅576px以下をモバイルとして判定
  window.innerWidth <= 576 ? recommendSp : recommendPc
);

// Fish残高のローディング状態（初期表示 + store状態）
const fishLoading = computed(() => initialFishLoading.value || storeFishLoading.value);

const baseFishRate = computed(() => {
  if (!fishList.value || fishList.value.length === 0) {
    return 200; // デフォルト値(1円200Fish)
  }

  // 各商品のFish/価格の比率を計算し、最も低い比率を取得
  const rates = fishList.value.map((fish) => {
    const rate = fish.amount / fish.price;
    return rate;
  });

  const minRate = Math.min(...rates);

  return minRate;
});

const calculateBonusFish = (fish: FishProduct): number => {
  const realFish = fish.price * baseFishRate.value;
  return Math.round(fish.amount - realFish);
};

// メソッド
const formatNumber = (value: number): string => value.toLocaleString("ja-JP");

const fetchFishProducts = async () => {
  isLoading.value = true;
  error.value = null;

  try {
    fishList.value = await nolaCoinClient.getFishProducts();
  } catch (err) {
    console.error("Fish商品の取得に失敗しました:", err);
    error.value = t("purchaseFish.error.loadFailed");
    fishList.value = []; // エラー時は空配列にして、エラー表示を優先
  } finally {
    isLoading.value = false;
  }
};

const retryFetchFishData = async () => {
  try {
    await store?.dispatch("fishModule/fetchFishData");
  } finally {
    initialFishLoading.value = false;
  }
};

const onClickPurchase = async (fishProduct: FishProduct) => {
  // アナリティクス: Fish商品購入ボタンクリックイベントの発火
  nolaAnalytics.logButtonEvent(`${NolaItemId.PurchaseFishPackage}_${fishProduct.price}`);

  try {
    const user = await Auth.currentUserPoolUser().catch(() => null);
    if (!user) {
      console.error(t("purchaseFish.error.authRequired"));
      return;
    }

    const { email } = user.attributes;
    const payload: CreateOneTimeCheckoutSessionInput = {
      email,
      itemId: fishProduct.id,
      successUrl: `${window.location.origin}/mypage`,
      cancelUrl: `${window.location.origin}/purchase-fish`,
    };

    const session = await billingClient.createOneTimeCheckoutSession(payload);
    window.location.href = session.url;
  } catch (err) {
    console.error("購入画面への遷移に失敗しました:", err);
    await showNotifyDialog({
      title: t("purchaseFish.error.purchaseFailedTitle"),
      content: t("purchaseFish.error.purchaseFailed"),
    });
  }
};

const onClickPremiumBanner = () => {
  // アナリティクス: プレミアム誘導バナークリックイベントの発火
  nolaAnalytics.logButtonEvent(NolaItemId.PurchaseFishPremiumBanner);
};

// ライフサイクルフック
onMounted(async () => {
  // Fish商品データを取得
  await fetchFishProducts();
  // MyPageと同様にFish残高データを取得
  try {
    await store?.dispatch("fishModule/fetchFishData");
  } finally {
    initialFishLoading.value = false;
  }
});

return { __sfc: true,nolaCoinClient, billingClient, instance, store, nolaAnalytics, t, fishList, isLoading, error, initialFishLoading, formattedCurrentFish, formattedPrepaidFish, formattedFreeFish, storeFishLoading, fishError, plan, isFreePlan, bannerImageUrl, fishLoading, baseFishRate, calculateBonusFish, formatNumber, fetchFishProducts, retryFetchFishData, onClickPurchase, onClickPremiumBanner, ChevronLeftIcon, PlusIcon, LoadingSpinner }
}

})